import detailMessage from '../detailMessage';
import React, { useState, useEffect } from 'react';
import {
  disableEmailMfa,
  disableTotpMfa,
  enableEmailMfa,
  enableTotpMfa,
  getMfaStatus,
  setupTotpMfa,
} from '../api';
import { useLang } from '../LangContext';

interface Props {
  token: string;
}

interface TotpSetup {
  secret: string;
  uri: string;
}

const MfaSettings = ({ token }: Props) => {
  const { t } = useLang();
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totpSetup, setTotpSetup] = useState<TotpSetup | null>(null);
  const [totpConfirmCode, setTotpConfirmCode] = useState('');
  const [totpConfirming, setTotpConfirming] = useState(false);
  const [emailDisablePassword, setEmailDisablePassword] = useState('');
  const [showEmailDisableForm, setShowEmailDisableForm] = useState(false);
  const [totpDisablePassword, setTotpDisablePassword] = useState('');
  const [showTotpDisableForm, setShowTotpDisableForm] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const status = await getMfaStatus(token);
        setEmailEnabled(!!status.mfa_email_enabled);
        setTotpEnabled(!!status.mfa_totp_enabled);
      } catch (err) {
        console.error('Error loading MFA status:', err);
      }
    })();
  }, [token]);

  const clearMessages = () => { setError(''); setSuccess(''); };

  const handleEnableEmail = async () => {
    setLoading(true);
    clearMessages();
    try {
      await enableEmailMfa(token);
      setEmailEnabled(true);
      setSuccess(t.mfaEmailEnabled);
    } catch (err) {
      setError(detailMessage(err, t.mfaFailedEnableEmailSettings));
    } finally {
      setLoading(false);
    }
  };

  const handleDisableEmail = async () => {
    setLoading(true);
    clearMessages();
    try {
      await disableEmailMfa(token, emailDisablePassword);
      setEmailEnabled(false);
      setShowEmailDisableForm(false);
      setEmailDisablePassword('');
      setSuccess(t.mfaEmailDisabled);
    } catch (err) {
      setError(detailMessage(err, t.mfaFailedDisableEmail));
    } finally {
      setLoading(false);
    }
  };

  const handleStartTotp = async () => {
    setLoading(true);
    clearMessages();
    try {
      const data = await setupTotpMfa(token);
      setTotpSetup(data);
      setTotpConfirmCode('');
    } catch (err) {
      setError(detailMessage(err, t.mfaFailedStartTotp));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmTotp = async () => {
    setTotpConfirming(true);
    setError('');
    try {
      await enableTotpMfa(token, totpConfirmCode);
      setTotpEnabled(true);
      setTotpSetup(null);
      setTotpConfirmCode('');
      setSuccess(t.mfaTotpEnabled);
    } catch (err) {
      setError(detailMessage(err, t.mfaInvalidCodeSettings));
    } finally {
      setTotpConfirming(false);
    }
  };

  const handleDisableTotp = async () => {
    setLoading(true);
    clearMessages();
    try {
      await disableTotpMfa(token, totpDisablePassword);
      setTotpEnabled(false);
      setShowTotpDisableForm(false);
      setTotpDisablePassword('');
      setSuccess(t.mfaTotpDisabled);
    } catch (err) {
      setError(detailMessage(err, t.mfaFailedDisableTotp));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="profile-section">
      <h3>{t.twoFactorHeading}</h3>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="mfa-methods">
        <div className="mfa-method-row">
          <div className="mfa-method-info">
            <span className="mfa-method-label">{t.mfaEmailMethodLabel}</span>
            <span className="mfa-method-desc">{t.mfaEmailMethodDesc}</span>
          </div>
          {emailEnabled ? (
            <div className="mfa-method-enabled">
              {!showEmailDisableForm ? (
                <button
                  className="button button-sm button-danger"
                  onClick={() => { setShowEmailDisableForm(true); clearMessages(); }}
                >
                  {t.mfaDisableButton}
                </button>
              ) : (
                <div className="mfa-disable-form">
                  <input
                    className="input"
                    type="password"
                    placeholder={t.mfaCurrentPasswordPlaceholder}
                    value={emailDisablePassword}
                    onChange={(e) => setEmailDisablePassword(e.target.value)}
                  />
                  <div className="mfa-disable-actions">
                    <button
                      className="button button-sm button-danger"
                      onClick={handleDisableEmail}
                      disabled={loading || !emailDisablePassword}
                    >
                      {loading ? t.mfaDisabling : t.mfaConfirmButton}
                    </button>
                    <button
                      className="button button-sm button-secondary"
                      onClick={() => { setShowEmailDisableForm(false); setEmailDisablePassword(''); }}
                    >
                      {t.cancelButton}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button className="button button-sm" onClick={handleEnableEmail} disabled={loading}>
              {t.mfaEnableButton}
            </button>
          )}
        </div>

        <div className="mfa-method-row">
          <div className="mfa-method-info">
            <span className="mfa-method-label">{t.mfaAppMethodLabel}</span>
            <span className="mfa-method-desc">{t.mfaAppMethodDesc}</span>
          </div>
          {totpEnabled ? (
            <div className="mfa-method-enabled">
              {!showTotpDisableForm ? (
                <button
                  className="button button-sm button-danger"
                  onClick={() => { setShowTotpDisableForm(true); clearMessages(); }}
                >
                  {t.mfaDisableButton}
                </button>
              ) : (
                <div className="mfa-disable-form">
                  <input
                    className="input"
                    type="password"
                    placeholder={t.mfaCurrentPasswordPlaceholder}
                    value={totpDisablePassword}
                    onChange={(e) => setTotpDisablePassword(e.target.value)}
                  />
                  <div className="mfa-disable-actions">
                    <button
                      className="button button-sm button-danger"
                      onClick={handleDisableTotp}
                      disabled={loading || !totpDisablePassword}
                    >
                      {loading ? t.mfaDisabling : t.mfaConfirmButton}
                    </button>
                    <button
                      className="button button-sm button-secondary"
                      onClick={() => { setShowTotpDisableForm(false); setTotpDisablePassword(''); }}
                    >
                      {t.cancelButton}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              {!totpSetup ? (
                <button className="button button-sm" onClick={handleStartTotp} disabled={loading}>
                  {t.mfaEnableButton}
                </button>
              ) : (
                <div className="totp-setup">
                  <p>{t.mfaTotpInstructionsSettings}</p>
                  <div className="totp-secret">{totpSetup.secret}</div>
                  <p className="mfa-hint">{t.mfaTotpHintSettings}</p>
                  <input
                    className="input"
                    placeholder={t.mfaCodePlaceholder}
                    value={totpConfirmCode}
                    onChange={(e) => setTotpConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                  />
                  <div className="mfa-disable-actions">
                    <button
                      className="button button-sm"
                      onClick={handleConfirmTotp}
                      disabled={totpConfirming || totpConfirmCode.length < 6}
                    >
                      {totpConfirming ? t.mfaVerifyingButton : t.mfaConfirmEnableButton}
                    </button>
                    <button
                      className="button button-sm button-secondary"
                      onClick={() => { setTotpSetup(null); setTotpConfirmCode(''); }}
                    >
                      {t.cancelButton}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MfaSettings;
